/**
 * Which key a file's parts are stating, and which of them are stating one at all.
 *
 * A MusicXML part prints the signature its own player reads, so a file of a transposing
 * score states several different signatures for the same bar and none of them is
 * necessarily the piece's. The document keeps one key per bar, at concert pitch, so
 * reading a score means choosing which part to believe and bringing its statement back
 * through that part's own transposition.
 *
 * The other half of the job is noticing the parts that are written with no signature at
 * all -- the horns and trumpets of a nineteenth-century score, and the timpani. In a file
 * they look exactly like a part in C major, so they can only be told apart by what the
 * rest of the score is doing: a part stating no sharps where the piece's key would have
 * given it some is a part that has none.
 */

import { toSoundingKey, toWrittenKey } from "../document/key";
import type { Transposition } from "../document/pitch";
import { transposes } from "../document/pitch";
import type { GlobalMeasure, KeyPrinting, KeySignature } from "../document/types";

/** What one part says about the key at one bar. */
export interface KeyStatement {
  readonly key: KeySignature | null;
  readonly transposition: Transposition;
}

/**
 * The key the piece is in at a bar, or `null` when no part states one there.
 *
 * A part at concert pitch is preferred over a transposing one that also states a key,
 * because its statement needs no conversion and so cannot be respelled on the way in: a
 * file whose first part is a clarinet and whose second is a flute takes the flute's five
 * flats over the clarinet's three, which are the same key spelled two ways.
 */
export function pieceKey(statements: readonly KeyStatement[]): KeySignature | null {
  let fallback: KeySignature | null = null;
  for (const statement of statements) {
    if (statement.key === null) continue;
    if (!transposes(statement.transposition)) return statement.key;
    fallback ??= toSoundingKey(statement.key, statement.transposition);
  }
  return fallback;
}

/** Fifths in the circle: the distance between the two spellings of one signature. */
const CIRCLE = 12;

/** What one stated signature says about how the part arrives at its signatures. */
function departure(stated: number, derived: number): KeyPrinting | null {
  if (stated === derived) return null;
  if (stated === 0) return "none";
  if (stated === derived + CIRCLE) return "sharps";
  if (stated === derived - CIRCLE) return "flats";
  // Anything else is a file disagreeing with itself -- a part in a key that is not the
  // piece's at all. The notes carry their own spelling and are read as they stand; the
  // signature goes back to the derived one, which is the only one the document can hold.
  return null;
}

/**
 * How a part arrives at the signatures it states, judged by every one of them.
 *
 * Three things a part can do instead of printing the derived signature are worth keeping,
 * and each shows up as the same kind of disagreement: it states nothing where the derived
 * key has sharps or flats, or it states the far side of the circle from the one derivation
 * reached -- F sharp where the piece is in G flat. The first bar that departs decides it,
 * and a part that agrees everywhere -- including one that states nothing at all, which is
 * most parts after their first bar -- is `derived` like everyone else.
 */
export function keyPrintingOf(
  stated: readonly KeyStatement[],
  measures: readonly GlobalMeasure[],
): KeyPrinting {
  let key: KeySignature | null = null;
  for (let index = 0; index < measures.length; index += 1) {
    key = measures[index]?.key ?? key;
    const here = stated[index];
    if (here === undefined || here.key === null || key === null) continue;
    // Against the transposition of that bar rather than the part's own: a player who has
    // picked another instrument up prints the signature it reads, and judging those bars
    // against the instrument they started on would read every one of them as a departure.
    const found = departure(here.key.fifths, toWrittenKey(key, here.transposition).fifths);
    if (found !== null) return found;
  }
  return "derived";
}
